import { useAuth0 } from '@auth0/auth0-react';
import {
  EllipsisVerticalIcon,
  LoaderCircleIcon,
  LogOutIcon,
  Settings2Icon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react';
import { Link } from 'react-router';

import { getEnv } from '@/env';
import { cn } from '@/shared/helpers';

import { Avatar, AvatarFallback, AvatarImage } from '../core/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../core/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '../core/sidebar';
import { Skeleton } from '../core/skeleton';

export function UserMenu() {
  const { isLoading, user, logout } = useAuth0();
  const { isMobile } = useSidebar();

  const userCard = (
    <>
      <Avatar className="items-center rounded-lg" aria-hidden>
        <AvatarImage src={user?.picture} alt={user?.name} />
        <AvatarFallback className="flex size-full items-center justify-center bg-sidebar-primary text-sidebar-primary-foreground">
          {isLoading ? (
            <LoaderCircleIcon className="size-5 animate-spin" />
          ) : (
            <UserIcon className="size-5" />
          )}
        </AvatarFallback>
      </Avatar>
      {isLoading ? (
        <div className="flex-1 space-y-1">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-3 w-44" />
        </div>
      ) : (
        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate text-sm font-semibold">{user?.name}</span>
          <span className="truncate text-xs">{user?.email}</span>
        </div>
      )}
    </>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {userCard}
              <EllipsisVerticalIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(
              'min-w-56 rounded-lg',
              isMobile && 'w-(--radix-dropdown-menu-trigger-width)',
            )}
            side={isMobile ? 'top' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm text-popover-foreground">
                {userCard}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            >
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
            {['LOCAL', 'DEV'].includes(getEnv()) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link to="/dev-config">
                      <SettingsIcon />
                      Developer config
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/features">
                      <Settings2Icon />
                      Features
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
